import { useMemo } from "react";
import type { AvailableTermsSetMap, SelectedTerms } from "@/features/productVariations/types";
import type { ProductVariation } from "@/types/productVariations";

type UseAttributeOptionsReturn = AvailableTermsSetMap;

const useAttributeOptions = (
  initialAvailableTermsSetMap: AvailableTermsSetMap,
  selectedTerms: SelectedTerms,
  attributeNames: string[],
  inStockVariations: ProductVariation[],
): UseAttributeOptionsReturn => {
  // 根據初始可用屬性、屬性名稱、已選擇的屬性以及有庫存的變體，過濾出可用的屬性選項
  const availableTermsSetMap: AvailableTermsSetMap = useMemo(() => {
    // 如果沒有選擇任何屬性，直接回傳初始值
    if (Object.values(selectedTerms).every((term) => !term)) {
      return initialAvailableTermsSetMap;
    }

    const result: AvailableTermsSetMap = {};
    attributeNames.forEach((attrName) => {
      result[attrName] = new Set<string>();
    }); // eg. { 烘焙度: Set{}, 規格: Set{} }

    const isSingleAttr = attributeNames.length === 1;

    inStockVariations.forEach((inStockV) => {
      inStockV.attributes.forEach(({ name: attrName, option: attrOption }) => {
        if (!result[attrName]) return;

        // 後台 API option 欄位 = 前台 API term 欄位
        if (isSingleAttr) {
          // 單一屬性：全部可用的 term 都加進去
          result[attrName].add(attrOption); // eg. { 烘焙度: 淺焙,中焙 }
        } else {
          // 多屬性：根據已選擇的屬性過濾
          const selected = selectedTerms[attrName];
          if (!selected || selected === attrOption) {
            // 若未選擇或符合已選擇的 term，則影響其他屬性：
            // 對於尚未選取的屬性，將該屬性的所有可用 term 加入。
            // 對於已選取的屬性，將該屬性在其他屬性中可用的 terms 加入；同時，若該屬性有其他可用 terms，也會將這些 terms 在其他屬性中的可用 terms 加入。
            inStockV.attributes.forEach(({ name: otherName, option: otherOption }) => {
              if (otherName !== attrName && result[otherName]) {
                result[otherName].add(otherOption);
              }
            });
          }
        }
      });
    });

    return result;
  }, [initialAvailableTermsSetMap, selectedTerms, attributeNames, inStockVariations]);

  return availableTermsSetMap;
};

export default useAttributeOptions;
